import React, { useState, useEffect, useRef, useCallback } from "react";
import styles from "./ProductGrid.module.css";
import debounce from "lodash/debounce";
import {
  Add as AddIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  FilterList as FilterListIcon,
  SearchOff as SearchOffIcon,
  Close as CloseIcon,
  TuneRounded as TuneIcon,
  WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";
import { getDelivery } from "../../utils/deliveryDateUtils";
import { addToCart } from "../../lib/addToCart";
import { useDispatch, useSelector, useStore } from "react-redux";
import { getBatchUnifiedDeliveryData } from "../../lib/unifiedDeliveryService";
import type { SortOption } from "../../utils/meilisearchClient";
import type { MeilisearchProduct } from "../../types-var/product";
import { DateService } from "../../utils/dateUtils";
import ProductImage from "../common/ProductImage/ProductImage";
import useLocalStorage from "../../hooks/useLocalStorage";
import Filters from "../Filters/Filters";

interface SortOptionUI {
  label: string;
  value: SortOption;
  icon?: React.ReactNode;
}

const SORT_OPTIONS: SortOptionUI[] = [
  {
    label: "Popularidad",
    value: "popularity:desc",
    icon: <FilterListIcon fontSize="small" />,
  },
  {
    label: "Precio: menor a mayor",
    value: "price:asc",
    icon: <ArrowUpIcon fontSize="small" />,
  },
  {
    label: "Precio: mayor a menor",
    value: "price:desc",
    icon: <ArrowDownIcon fontSize="small" />,
  },
  {
    label: "Nombre: A-Z",
    value: "name:asc",
    icon: <ArrowUpIcon fontSize="small" />,
  },
  {
    label: "Nombre: Z-A",
    value: "name:desc",
    icon: <ArrowDownIcon fontSize="small" />,
  },
];

interface ProductGridProps {
  products: MeilisearchProduct[];
  toggleMiniCart: () => void;
  availableFilters: { [key: string]: string[] };
  filterStyles: { [key: string]: string };
  filterCounts: { [key: string]: { [value: string]: number } };
  onSortChange: (sort: SortOption) => void;
  currentSort: SortOption;
  categoryContext?: {
    id: number;
    url: string;
    level: number;
  };
  totalHits?: number;
  isLoading?: boolean;
  query?: string;
  onClearFilters?: () => void;
  filters?: { [key: string]: string[] };
  setFilters?: (filters: { [key: string]: string[] }) => void;
}

interface Color {
  name: string;
  colorHex: string | null;
}

const ProductSkeleton: React.FC = () => (
  <div
    className={styles.productSkeletonCard}
    role="presentation"
    aria-hidden="true"
  >
    <div className={styles.skeletonImage} style={{ height: "290px" }}></div>
    <div className={styles.skeletonInfo}>
      <div className={styles.skeletonButton}></div>
      <div className={styles.skeletonPriceContainer}>
        <div className={styles.skeletonPrice}></div>
        <div className={styles.skeletonOldPrice} style={{ width: "60%" }}></div>
      </div>
      <div className={styles.skeletonName}></div>
      <div className={styles.skeletonDelivery}></div>
    </div>
  </div>
);

const EmptyState: React.FC<{
  query?: string;
  onClearFilters?: () => void;
  hasFilters: boolean;
}> = ({ query, onClearFilters, hasFilters }) => (
  <div
    className={styles.noProductsMessage}
    role="region"
    aria-label="No products found"
  >
    <SearchOffIcon className={styles.noProductsIcon} aria-hidden="true" />
    <h2 className={styles.noProductsTitle}>
      {query
        ? `No encontramos resultados para "${query}"`
        : "No hay productos disponibles"}
    </h2>
    <p className={styles.noProductsText}>
      {hasFilters
        ? "Los filtros actuales pueden estar limitando los resultados. Prueba quitando algunos filtros para ver más productos."
        : query
        ? "Intenta con diferentes palabras clave o verifica la ortografía."
        : "No encontramos productos para esta categoría en este momento."}
    </p>
    {hasFilters && onClearFilters && (
      <button
        className={styles.noProductsButton}
        onClick={onClearFilters}
        aria-label="Eliminar todos los filtros"
      >
        <CloseIcon fontSize="small" style={{ marginRight: "4px" }} />
        Eliminar filtros
      </button>
    )}
    <div className={styles.whatsappRequestContainer}>
      <WhatsAppIcon className={styles.whatsappIcon} />
      <p className={styles.whatsappText}>
        ¿No encuentras lo que buscas? Podemos ayudarte a conseguirlo.
      </p>
      <a
        href="https://wa.me/51906869576?text=Hola,%20estoy%20buscando%20un%20producto%20que%20no%20encuentro%20en%20la%20web:%20"
        target="_blank"
        rel="noopener noreferrer"
        className={styles.whatsappLink}
      >
        Contactar por WhatsApp
      </a>
    </div>
  </div>
);

const WhatsAppAnnouncementCard: React.FC = () => (
  <div className={styles.whatsappCard}>
    <div className={styles.whatsappCardContent}>
      <div className={styles.whatsappCardIcon}>
        <WhatsAppIcon className={styles.whatsappCardIconSvg} />
      </div>
      <h3 className={styles.whatsappCardTitle}>
        ¿No encuentras lo que buscas?
      </h3>
      <p className={styles.whatsappCardText}>
        Pregúntanos y haremos lo posible por conseguir tu producto.
      </p>
      <a
        href="https://wa.me/51906869576?text=Hola,%20estoy%20buscando%20un%20producto%20que%20no%20encuentro%20en%20la%20web:%20"
        target="_blank"
        rel="noopener noreferrer"
        className={`buttonMain ${styles.whatsappCardButton}`}
      >
        Contactar por WhatsApp
      </a>
    </div>
  </div>
);

const formatPrice = (price: number): string => {
  return `S/ ${Number(price).toFixed(2)}`;
};

const getPriceInfo = (product: MeilisearchProduct) => {
  let centralPrice: string;
  let oldPrice: string | null = null;
  let priceRange: string | null = null;

  if (product.type === "simple") {
    if (product.price.discount?.newPrice) {
      centralPrice = `Ahora ${formatPrice(product.price.discount.newPrice)}`;
      oldPrice = formatPrice(product.price.regPrice);
    } else {
      centralPrice = formatPrice(product.price.regPrice);
    }
  } else {
    if (product.price.discount?.newPrice) {
      centralPrice = `Ahora ${formatPrice(product.price.discount.newPrice)}`;
      oldPrice = formatPrice(product.price.regPrice);
    } else if (product.price.priceRange) {
      centralPrice = `Desde ${formatPrice(product.price.priceRange.min)}`;
      if (product.price.priceRange.min !== product.price.priceRange.max) {
        priceRange = `Opciones entre ${formatPrice(
          product.price.priceRange.min
        )} - ${formatPrice(product.price.priceRange.max)}`;
      }
    } else {
      centralPrice = formatPrice(product.price.regPrice);
    }
  }

  return { centralPrice, oldPrice, priceRange };
};

const getProductColors = (product: MeilisearchProduct): Color[] => {
  return product.attributes
    .filter((attr) => attr.name === "Color")
    .flatMap((attr) =>
      attr.values
        .map(
          (value): Color => ({
            name: value.name,
            colorHex: value.colorHex || null,
          })
        )
        .filter((color): color is Color => color.colorHex !== null)
    );
};

const transformProducts = async (
  products: MeilisearchProduct[],
  destinationLeadTimeZoneId?: string,
  destinationMethodsZoneId?: string
) => {
  if (!destinationLeadTimeZoneId || !destinationMethodsZoneId) {
    console.log("No location data available, skipping delivery calculations");
    return products.map((product) => ({
      ...product,
      fastestDeliveryDate: null,
      expressDelivery: false,
    }));
  }

  // Create combinations for unified API
  const deliveryCombinations = products.map((product) => ({
    originLeadTimeZoneId:
      product.partner?.originZones?.deliveryLeadTimeZone?.documentId,
    originFeeZoneId: product.partner?.originZones?.deliveryFeeZone?.documentId,
    destinationLeadTimeZoneId,
    destinationFeeZoneId: destinationMethodsZoneId,
  }));

  try {
    // Use our new unified service instead of two separate calls
    const deliveryDataResults = await getBatchUnifiedDeliveryData(
      deliveryCombinations
    );

    return products.map((product, index) => {
      // Extract data from the unified response
      const methods = deliveryDataResults[index].deliveryMethods;
      const opsLeadTime = deliveryDataResults[index].opsLeadTime;

      const deliveryOptions = {
        expressDelivery: product.logistics?.expressDelivery || false,
        limitTime: product.logistics?.limitTime || "00:00",
        daysToDeliver:
          typeof product.logistics?.minDaysToDeliver !== "undefined"
            ? product.logistics.minDaysToDeliver
            : product.logistics?.daysToDeliver || 0,
        opsLeadTime,
        daysOpen: product.partner?.daysOpen || [],
      };

      const deliveryDates = getDelivery(deliveryOptions, new Date(), {
        express: !!methods.express,
        rapido: !!methods.rapido,
        standard: !!methods.standard,
      });

      let fastestDeliveryDate = null;
      if (deliveryDates.express.isAvailable) {
        fastestDeliveryDate = `¡Recíbelo hoy mismo! Te quedan ${deliveryDates.express.countdown}`;
      } else if (deliveryDates.rapido.isAvailable) {
        const rapidoDate = deliveryDates.rapido.availableDates[0];
        fastestDeliveryDate = `Recíbelo ${
          rapidoDate
            ? `el ${DateService.formatDeliveryDateForDisplay(rapidoDate)}`
            : "entre mañana y pasado"
        }. Te quedan ${deliveryDates.rapido.countdown}`;
      } else if (deliveryDates.standard.startDate) {
        fastestDeliveryDate = `Recíbelo a partir del ${DateService.formatDeliveryDateForDisplay(
          deliveryDates.standard.startDate
        )}`;
      }

      return {
        ...product,
        fastestDeliveryDate,
        deliveryDates,
        expressDelivery: deliveryDates?.express?.isAvailable,
      };
    });
  } catch (error) {
    console.error("Error fetching delivery data:", error);
    return products.map((product) => ({
      ...product,
      fastestDeliveryDate: null,
      expressDelivery: false,
    }));
  }
};

const ProductGrid: React.FC<ProductGridProps> = ({
  products,
  toggleMiniCart,
  availableFilters,
  filterStyles,
  filterCounts,
  onSortChange,
  currentSort,
  categoryContext,
  totalHits = 0,
  isLoading = false,
  query,
  onClearFilters,
  filters = {},
  setFilters,
}) => {
  const dispatch = useDispatch();
  const store = useStore();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const sortButtonRef = useRef<HTMLDivElement>(null);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState<number | null>(
    null
  );
  const [selectedSort, setSelectedSort] = useState(
    SORT_OPTIONS.find((option) => option.value === currentSort) ||
      SORT_OPTIONS[0]
  );
  const [productsWithDelivery, setProductsWithDelivery] = useState<any[]>([]);
  const destinationLeadTimeZoneId = useSelector(
    (state: any) => state.location.deliveryLeadTimeZone?.documentId
  );
  const destinationMethodsZoneId = useSelector(
    (state: any) => state.location.deliveryFeeZone?.documentId
  );
  const isFetchingRef = useRef(false);
  const [savedSort, setSavedSort] = useLocalStorage<SortOption>(
    "productGridSortPreference",
    "popularity:desc"
  );
  const hasActiveFilters = Object.values(availableFilters || {}).some(
    (filter) => filter.length > 0
  );

  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const activeFiltersCount = Object.entries(filters)
    .filter(([_, values]) => Array.isArray(values) && values.length > 0)
    .reduce((count, [_, values]) => count + values.length, 0);

  const toggleMobileFilter = () => {
    setIsMobileFilterOpen(!isMobileFilterOpen);
  };

  const handleMobileFilterChange = (newFilters: {
    [key: string]: string[];
  }) => {
    if (setFilters) {
      setFilters(newFilters);
    }
    setIsMobileFilterOpen(false);
  };

  const closeMobileFilter = () => {
    setIsMobileFilterOpen(false);
  };

  const debouncedSaveScroll = useCallback(
    debounce(() => {
      const currentPath = window.location.pathname + window.location.search;
      sessionStorage.setItem("lastPath", currentPath);
      sessionStorage.setItem("scrollPosition", window.scrollY.toString());
    }, 100),
    []
  );

  const saveScrollPosition = useCallback(() => {
    debouncedSaveScroll();
  }, [debouncedSaveScroll]);

  useEffect(() => {
    const lastPath = sessionStorage.getItem("lastPath");
    const currentPath = window.location.pathname + window.location.search;
    const savedPosition = sessionStorage.getItem("scrollPosition");

    if (lastPath === currentPath && savedPosition) {
      window.scrollTo(0, parseInt(savedPosition));
    }
  }, []);

  useEffect(() => {
    window.addEventListener("beforeunload", saveScrollPosition);
    return () => {
      window.removeEventListener("beforeunload", saveScrollPosition);
      saveScrollPosition();
    };
  }, [saveScrollPosition]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        sortButtonRef.current &&
        !sortButtonRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
        setFocusedOptionIndex(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (savedSort && savedSort !== currentSort && !isLoading) {
      onSortChange(savedSort);
    }
  }, [savedSort, currentSort, onSortChange, isLoading]);

  useEffect(() => {
    const newSelectedSort =
      SORT_OPTIONS.find((option) => option.value === currentSort) ||
      SORT_OPTIONS[0];
    setSelectedSort(newSelectedSort);
  }, [currentSort]);

  useEffect(() => {
    let isMounted = true;

    const fetchDeliveryData = async () => {
      if (isFetchingRef.current || !products.length) return;

      isFetchingRef.current = true;

      try {
        const productsData = await transformProducts(
          products,
          destinationLeadTimeZoneId,
          destinationMethodsZoneId
        );

        if (isMounted) {
          window.requestAnimationFrame(() => {
            setProductsWithDelivery(productsData);
          });
        }
      } catch (error) {
        console.error("Failed to fetch batch delivery data:", error);
        if (isMounted) {
          setProductsWithDelivery(products);
        }
      } finally {
        isFetchingRef.current = false;
      }
    };

    fetchDeliveryData();

    return () => {
      isMounted = false;
    };
  }, [products, destinationLeadTimeZoneId, destinationMethodsZoneId]);

  const handleSortChange = (option: SortOptionUI) => {
    setIsDropdownOpen(false);
    setSelectedSort(option);
    setFocusedOptionIndex(null);

    setSavedSort(option.value);

    onSortChange(option.value);

    sortButtonRef.current?.focus();
  };

  const handleAddToCart = async (product: any) => {
    try {
      if (product.type === "simple") {
        const customizableOptions = product.customizableOptions?.map(
          (option: any) => ({
            id: option.id || `opt-${Date.now()}`,
            type: option.__component || "custom",
            label: option.title || "Custom Option",
            value: option.defaultValue || null,
            priceSurcharge: option.priceSurcharge || 0,
            options: option.options || [],
          })
        );

        const groupingAttributes = product.attributes
          ?.filter((attr: any) => attr.isGroupingAttribute)
          ?.map((attr: any) => ({
            name: attr.name,
            value: attr.values?.[0]?.name || "",
          }));

        await addToCart(
          product.id,
          1,
          {
            cartCustomId: product.sku || `custom-${Date.now()}`,
            customizableOptions,
            groupingAttributes,
            productData: product,
          },
          dispatch,
          toggleMiniCart,
          store.getState
        );
      } else {
        window.location.href = `/p/${product.url}`;
      }
    } catch (error) {
      console.error("Failed to add product to cart:", error);
      alert("Failed to add product to cart. Please try again.");
    }
  };

  const handleSortKeyDown = (event: React.KeyboardEvent) => {
    if (!isDropdownOpen) {
      if (
        event.key === "Enter" ||
        event.key === " " ||
        event.key === "ArrowDown"
      ) {
        event.preventDefault();
        setIsDropdownOpen(true);
        setFocusedOptionIndex(0);

        setTimeout(() => {
          const firstOption = document.getElementById("sort-option-0");
          firstOption?.focus();
        }, 10);
      }
      return;
    }

    switch (event.key) {
      case "Escape":
        event.preventDefault();
        setIsDropdownOpen(false);
        setFocusedOptionIndex(null);
        sortButtonRef.current?.focus();
        break;
      case "ArrowDown":
        event.preventDefault();
        setFocusedOptionIndex((prevIndex) => {
          const nextIndex =
            prevIndex !== null ? (prevIndex + 1) % SORT_OPTIONS.length : 0;

          const nextElement = document.getElementById(
            `sort-option-${nextIndex}`
          );
          nextElement?.focus();

          return nextIndex;
        });
        break;
      case "ArrowUp":
        event.preventDefault();
        setFocusedOptionIndex((prevIndex) => {
          const nextIndex =
            prevIndex !== null
              ? (prevIndex - 1 + SORT_OPTIONS.length) % SORT_OPTIONS.length
              : SORT_OPTIONS.length - 1;

          const nextElement = document.getElementById(
            `sort-option-${nextIndex}`
          );
          nextElement?.focus();

          return nextIndex;
        });
        break;
    }
  };

  const getSortDirection = (sortValue: string): "asc" | "desc" | null => {
    if (sortValue.endsWith(":asc")) return "asc";
    if (sortValue.endsWith(":desc")) return "desc";
    return null;
  };

  const getSortAriaLabel = (option: SortOptionUI): string => {
    const direction = getSortDirection(option.value);
    return `Ordenar por ${option.label} ${
      direction === "asc"
        ? "ascendente"
        : direction === "desc"
        ? "descendente"
        : ""
    }`;
  };

  const ResponsiveProductImage: React.FC<{
    src?: string;
    alt: string;
    className: string;
  }> = ({ src, alt, className }) => {
    const isMobile = window.innerWidth <= 768;
    const width = isMobile ? 150 : 250;
    const height = isMobile ? 174 : 290;
    const srcSet = src ? `${src} ${width}w, ${src} ${height}w` : undefined;

    return (
      <div className={styles.productImageContainer}>
        {src ? (
          <ProductImage
            src={src}
            srcSet={srcSet}
            width={width}
            height={height}
            alt={alt}
            className={className}
            loading="lazy"
          />
        ) : (
          <div
            className={styles.imagePlaceholder}
            aria-label={`Imagen de ${alt}`}
          />
        )}
      </div>
    );
  };

  const renderEmptyState = () => (
    <EmptyState
      query={query}
      onClearFilters={onClearFilters}
      hasFilters={hasActiveFilters}
    />
  );

  const renderMobileFilterButton = () => {
    return (
      <button
        className={styles.mobileFilterButton}
        onClick={toggleMobileFilter}
        aria-label={`${isMobileFilterOpen ? "Cerrar" : "Abrir"} filtros`}
      >
        <TuneIcon fontSize="small" aria-hidden="true" />
        <span>Filtros</span>
        {activeFiltersCount > 0 && (
          <span className={styles.filterBadge}>{activeFiltersCount}</span>
        )}
      </button>
    );
  };

  if (isLoading) {
    return (
      <div className={styles.productGridContainer} aria-busy="true">
        <div className={styles.headerContainer}>
          <p className={styles.productCount}>Cargando productos...</p>
          <div className={styles.sortDropdownContainer}>
            <span className={styles.sortLabel}>Ordenar por:</span>
            <div className={`${styles.sortDropdown} ${styles.disabled}`}>
              <span className={styles.placeholderText}>Cargando...</span>
            </div>
          </div>
        </div>
        <div className={styles.productsList}>
          {Array.from({ length: 12 }).map((_, index) => (
            <ProductSkeleton key={`skeleton-${index}`} />
          ))}
        </div>
      </div>
    );
  }

  if (!isLoading && products.length === 0) {
    return (
      <div className={styles.productGridContainer}>
        <div className={styles.headerContainer}>
          <p className={styles.productCount}>0 productos encontrados</p>
          <div className={styles.sortDropdownContainer}>
            <span className={styles.sortLabel}>Ordenar por:</span>
            <div
              className={`${styles.sortDropdown} ${styles.disabled}`}
              aria-disabled="true"
            >
              <span>{selectedSort.label}</span>
            </div>
          </div>
        </div>
        {renderEmptyState()}
      </div>
    );
  }

  return (
    <div className={styles.productGridContainer}>
      <div className={styles.headerContainer}>
        <div className={styles.productCountWrapper}>
          <p className={styles.productCount} role="status">
            {totalHits > 0
              ? `${totalHits} productos encontrados`
              : `${products.length} productos encontrados`}
          </p>
        </div>

        <div className={styles.controlsContainer}>
          <div className={styles.mobileFilterButtonWrapper}>
            {renderMobileFilterButton()}
          </div>

          <div className={styles.sortDropdownContainer}>
            <label id="sort-label" className={styles.sortLabel}>
              Ordenar por:
            </label>

            <div
              ref={sortButtonRef}
              className={`${styles.sortDropdown} ${
                isDropdownOpen ? styles.open : ""
              }`}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              onKeyDown={handleSortKeyDown}
              tabIndex={0}
              role="combobox"
              aria-expanded={isDropdownOpen}
              aria-controls="sort-options-list"
              aria-labelledby="sort-label"
              aria-haspopup="listbox"
            >
              {selectedSort.icon && (
                <span
                  className={`${styles.sortIcon} ${
                    getSortDirection(selectedSort.value) === "asc"
                      ? styles.sortIconAsc
                      : ""
                  }`}
                  aria-hidden="true"
                >
                  {selectedSort.icon}
                </span>
              )}
              <span className={styles.sortLabelMobile}>Ordenar</span>
              <span className={styles.sortValue}>{selectedSort.label}</span>
              <span className={styles.arrowIcon} aria-hidden="true"></span>

              {isDropdownOpen && (
                <div
                  className={styles.sortDropdownBackdrop}
                  onClick={() => setIsDropdownOpen(false)}
                  aria-hidden="true"
                />
              )}

              <ul
                id="sort-options-list"
                className={`${styles.dropdownList} ${
                  isDropdownOpen ? styles.show : ""
                }`}
                role="listbox"
                aria-labelledby="sort-label"
                ref={dropdownRef}
              >
                {SORT_OPTIONS.map((option, optionIndex) => (
                  <li
                    key={option.value}
                    id={`sort-option-${optionIndex}`}
                    role="option"
                    aria-selected={selectedSort.value === option.value}
                    aria-label={getSortAriaLabel(option)}
                    tabIndex={isDropdownOpen ? 0 : -1}
                    onClick={() => handleSortChange(option)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSortChange(option);
                      }
                    }}
                    className={
                      selectedSort.value === option.value ? styles.selected : ""
                    }
                  >
                    {option.icon && (
                      <span
                        className={`${styles.optionIcon} ${
                          getSortDirection(option.value) === "asc"
                            ? styles.iconAsc
                            : ""
                        }`}
                        aria-hidden="true"
                      >
                        {option.icon}
                      </span>
                    )}
                    <span>{option.label}</span>
                    {selectedSort.value === option.value && (
                      <span className={styles.checkmark} aria-hidden="true">
                        ✓
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {isMobileFilterOpen && (
        <>
          <div className={styles.mobileFilterDrawer}>
            <div className={styles.mobileFilterHeader}>
              <h3>Filtros</h3>
              <button
                className={styles.closeFilterButton}
                onClick={closeMobileFilter}
                aria-label="Cerrar filtros"
              >
                <CloseIcon />
              </button>
            </div>
            <div className={styles.mobileFilterContent}>
              <Filters
                availableFilters={availableFilters}
                filters={filters}
                filterStyles={filterStyles}
                setFilters={handleMobileFilterChange}
                filterCounts={filterCounts}
                currentCategoryLevel={categoryContext?.level}
                initiallyOpen={true}
                isMobile={true}
              />
            </div>
          </div>
          <div
            className={`${styles.backdropOverlay} ${styles.visible}`}
            onClick={closeMobileFilter}
            aria-hidden="true"
          />
        </>
      )}

      <div
        className={styles.productsList}
        role="region"
        aria-label="Lista de productos"
      >
        {productsWithDelivery.map((product) => {
          const imageUrl = product.images[0]?.small;
          const { centralPrice, oldPrice, priceRange } = getPriceInfo(product);
          const colors = getProductColors(product);
          const earliestDeliveryDate = product.fastestDeliveryDate;
          const expressDelivery = product.expressDelivery;

          return (
            <div key={product.id} className={styles.productCard} tabIndex={0}>
              <a
                href={`/p/${product.url}`}
                className={styles.productLink}
                aria-label={product.name}
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest("button")) {
                    e.preventDefault();
                  }
                }}
              >
                <ResponsiveProductImage
                  src={imageUrl}
                  alt={product.name}
                  className={styles.productImage}
                />

                <button
                  className="buttonCatPage"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (product.type === "configurable") {
                      window.location.href = `/p/${product.url}`;
                    } else {
                      handleAddToCart(product);
                    }
                  }}
                  aria-label={
                    product.type === "configurable"
                      ? `Ver opciones de ${product.name}`
                      : `Añadir ${product.name} al carrito`
                  }
                >
                  {product.type !== "configurable" && (
                    <AddIcon className="icon" aria-hidden="true" />
                  )}
                  {product.type === "configurable" ? "Opciones" : "Añadir"}
                </button>

                <div className={styles.productPriceContainer}>
                  <p
                    className={`${styles.centralPrice} ${
                      oldPrice ? styles.discountedPrice : styles.regularPrice
                    }`}
                  >
                    {centralPrice}
                  </p>
                  {oldPrice && (
                    <p
                      className={styles.oldPrice}
                      aria-label={`Precio anterior: ${oldPrice}`}
                    >
                      <s>{oldPrice}</s>
                    </p>
                  )}
                </div>
                {priceRange && (
                  <p className={styles.priceRange}>{priceRange}</p>
                )}

                {expressDelivery && (
                  <span className={styles.expressLabel}>Express</span>
                )}

                {earliestDeliveryDate && (
                  <p className={styles.fastestDeliveryDate}>
                    {earliestDeliveryDate}
                  </p>
                )}

                <div className={styles.productInfoContainer}>
                  <p className={styles.productName}>{product.name}</p>
                </div>
              </a>
              {colors && colors.length > 0 && (
                <div
                  className={styles.colorOptions}
                  aria-label={`${colors.length} colores disponibles: ${colors
                    .map((c) => c.name)
                    .join(", ")}`}
                >
                  {colors.map((color: Color, index: number) => {
                    // Add a border if the color is white
                    const hex = color.colorHex?.replace("#", "").toLowerCase();
                    const isWhite =
                      hex === "fff" ||
                      hex === "ffffff" ||
                      hex === "fffff" ||
                      hex === "fff" ||
                      hex === "ffffff";
                    return (
                      <span
                        key={`${color.name}-${index}`}
                        className={styles.colorCircle}
                        style={{
                          backgroundColor: color.colorHex || undefined,
                          border: isWhite
                            ? "0.5px solid #ccc"
                            : "0.5px solid #fff",
                        }}
                        title={color.name}
                        aria-hidden="true"
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        <WhatsAppAnnouncementCard />
      </div>
    </div>
  );
};

export default React.memo(ProductGrid, (prevProps, nextProps) => {
  return (
    prevProps.products.length === nextProps.products.length &&
    prevProps.currentSort === nextProps.currentSort &&
    prevProps.totalHits === nextProps.totalHits &&
    prevProps.isLoading === nextProps.isLoading &&
    Object.keys(prevProps.availableFilters).length ===
      Object.keys(nextProps.availableFilters).length &&
    JSON.stringify(prevProps.filters) === JSON.stringify(nextProps.filters)
  );
});
